import { centsToEuros, eurosToCents } from '@folio/utils/amount-utils';
import { useQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  type PublicQuoteConfig,
} from 'korint-sdk';
import { useMemo } from 'react';

export type ManualComponentFormMap = Record<string, string | number | undefined>;

type ManualComponentValueTypesMap = Record<string, 'ABSOLUTE' | 'PERCENTAGE'>;

export type ManualComponentValuesMap = Record<
  string,
  number | { amountPreFeesAndTaxes: number; totalFees: number; totalTaxes: number }
>;

function parseFormValue(value: string | number | undefined): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value !== '') return Number(value);
  return Number.NaN;
}

export function useManualComponentValueTypes(
  productId: string,
  activeTierId: string | undefined,
): ManualComponentValueTypesMap {
  const { data: config } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId),
  });

  return useMemo(() => {
    const publicQuoteConfig = config?.publicQuoteConfig as PublicQuoteConfig | undefined;
    const tier = publicQuoteConfig?.tiers.find(t => t.id === activeTierId);
    const valueTypes: ManualComponentValueTypesMap = {};
    for (const component of tier?.manualComponents ?? []) {
      valueTypes[component.id] = component.valueType;
    }
    return valueTypes;
  }, [config, activeTierId]);
}

/** Form values (euros / percentage) → backend payload (cents / percentage). */
export function toManualComponentPayload(
  values: ManualComponentFormMap | undefined,
  valueTypes?: ManualComponentValueTypesMap,
): Record<string, number> {
  const payload: Record<string, number> = {};
  if (!values) return payload;

  for (const [id, value] of Object.entries(values)) {
    const parsed = parseFormValue(value);
    if (Number.isNaN(parsed)) continue;

    if (valueTypes?.[id] === 'ABSOLUTE') {
      const cents = eurosToCents(parsed);
      if (cents !== undefined) payload[id] = cents;
    } else {
      payload[id] = parsed;
    }
  }
  return payload;
}

type SavedQuoteComponent = {
  id: string;
  premium?: {
    amountPreFeesAndTaxes?: number;
    totalFees?: number;
    totalTaxes?: number;
    amountWithFeesAndTaxes?: number;
  };
};

type ManualComponentConfigForPrefill = {
  id: string;
  valueType: 'ABSOLUTE' | 'PERCENTAGE';
  editableFor?: string[];
  required?: boolean;
  referenceComponentIds?: string[];
  referenceAmountFields?: string[];
};

/**
 * Prefill form values from a saved quote so a different firm type (e.g.
 * retail editing a wholesale-quoted policy) can re-submit without losing
 * prior values. ABSOLUTE reads `amountWithFeesAndTaxes` (sums all three
 * premium fields so PERIL / FEE / TAX round-trip cleanly). PERCENTAGE
 * reverse-engineers the rate from the applied amount and the reference
 * component(s).
 */
export function fromSavedManualComponents(
  quoteComponents: SavedQuoteComponent[],
  manualComponentsConfig: ManualComponentConfigForPrefill[],
): Record<string, number> {
  const defaults: Record<string, number> = {};
  const componentsById = new Map(quoteComponents.map(c => [c.id, c]));

  for (const config of manualComponentsConfig) {
    const saved = componentsById.get(config.id);
    if (!saved?.premium) continue;

    if (config.valueType === 'ABSOLUTE') {
      const euros = centsToEuros(saved.premium.amountWithFeesAndTaxes);
      if (euros !== undefined) defaults[config.id] = euros;
      continue;
    }

    const refIds = config.referenceComponentIds ?? [];
    const refFields = config.referenceAmountFields ?? [];
    if (refIds.length === 0 || refFields.length === 0) continue;

    let referenceTotal = 0;
    for (const refId of refIds) {
      const refComponent = componentsById.get(refId);
      if (!refComponent?.premium) continue;
      for (const field of refFields) {
        const value = refComponent.premium[field as keyof typeof refComponent.premium];
        if (typeof value === 'number') referenceTotal += value;
      }
    }
    if (referenceTotal === 0) continue;

    const applied = saved.premium.amountWithFeesAndTaxes ?? 0;
    if (applied === 0) continue;
    defaults[config.id] = Math.round((applied / referenceTotal) * 100);
  }
  return defaults;
}

export function useManualComponentsConfig(
  productId: string,
  activeTierId: string | undefined,
): ManualComponentConfigForPrefill[] {
  const { data: config } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId),
  });

  return useMemo(() => {
    const publicQuoteConfig = config?.publicQuoteConfig as PublicQuoteConfig | undefined;
    const tier = publicQuoteConfig?.tiers.find(t => t.id === activeTierId);
    return (tier?.manualComponents ?? []) as ManualComponentConfigForPrefill[];
  }, [config, activeTierId]);
}

export function serializeManualValues(
  values: ManualComponentValuesMap | Record<string, number> | undefined,
): string | undefined {
  if (!values || Object.keys(values).length === 0) return undefined;
  return JSON.stringify({ values });
}

/**
 * `editableFor` gates which firm types can edit a manual component's input.
 * Undefined = any firm type can edit.
 */
export function isComponentEditableBy(
  component: { editableFor?: string[] },
  firmType: string | undefined,
): boolean {
  if (!firmType) return false;
  if (!component.editableFor) return true;
  return component.editableFor.includes(firmType);
}
